// src/services/api/mapbox.ts
import mapboxgl from 'mapbox-gl';
import { config } from '@/shared/utils/config';

// Initialize Mapbox with token
mapboxgl.accessToken = config.mapbox.token;

// Mapbox service functions
export const mapboxService = {
  // Create a new map instance
  createMap: (containerElement: string, options: Partial<mapboxgl.MapOptions> = {}): mapboxgl.Map => {
    return new mapboxgl.Map({
      container: containerElement,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [0, 20],
      zoom: 1.5,
      projection: 'globe',
      ...options
    });
  },
  
  // Add a marker to the map
  addMarker: (map: mapboxgl.Map, lngLat: [number, number], options: { color?: string, popup?: string } = {}) => {
    const { color = '#FF0000', popup } = options;
    
    // Create marker element
    const marker = new mapboxgl.Marker({ color })
      .setLngLat(lngLat)
      .addTo(map);
    
    // Add popup if provided
    if (popup) {
      const popupInstance = new mapboxgl.Popup({ offset: 25 })
        .setHTML(popup);
      
      marker.setPopup(popupInstance);
    }
    
    return marker;
  },
  
  // Fly to a location
  flyTo: (map: mapboxgl.Map, lngLat: [number, number], zoom: number = 9) => {
    map.flyTo({
      center: lngLat,
      zoom,
      essential: true
    });
  },
  
  // Geocode an address to coordinates
  geocode: async (address: string): Promise<[number, number] | null> => {
    try {
      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`;
      
      // Ensure token is a string
      const token = mapboxgl.accessToken || '';
      
      const params = new URLSearchParams({
        access_token: token,
        limit: '1'
      });
      
      // Define types for the Mapbox API response
      interface MapboxFeature {
        center: [number, number];
        // Add other properties you might need from a feature, e.g., place_name: string;
      }

      interface MapboxGeocodingResponse {
        features: MapboxFeature[];
        // Add other top-level properties from the response if needed, e.g., attribution: string;
      }
      
      const response = await fetch(`${endpoint}?${params}`);
      const data = await response.json() as MapboxGeocodingResponse; // Type assertion
      
      if (data.features && data.features.length > 0) {
        const firstFeature = data.features[0];
        if (firstFeature && firstFeature.center) {
          const [lng, lat] = firstFeature.center;
          return [lng, lat];
        }
      }
      
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  }
};

export default mapboxService;
